import Phaser from 'phaser';
import { Capacitor } from '@capacitor/core';
import { GAME_WIDTH, GAME_HEIGHT } from './config';
import { BootScene } from './scenes/BootScene';
import { MenuScene } from './scenes/MenuScene';
import { GameScene } from './scenes/GameScene';
import { CodexScene } from './scenes/CodexScene';
import { ShopScene } from './scenes/ShopScene';
import { SettingsScene } from './scenes/SettingsScene';
import { HelpScene } from './scenes/HelpScene';
import { DomOverlayHost } from './ui/dom/DomOverlay';

// Install the DOM overlay host once before Phaser boots. Every DOM overlay
// (HubOverlay, DraftOverlay, GameOverOverlay, …) mounts inside #dom-ui-root.
DomOverlayHost.install();

// Platform-conditional renderer settings. On the iOS Capacitor shell we want
// every premium tweak (HiDPI canvas, 120Hz ProMotion). On the web build the
// same combo turned the game into a slideshow on Retina laptops because
// (a) the canvas was rendering ~4× the pixel count under expensive
// backdrop-filter glass overlays and (b) Phaser was being asked to push 120
// frames a second on hardware that couldn't keep up. The web therefore caps
// at 60fps and renders at logical pixels — slightly less crisp on a 5K
// display, but smooth and usable.
const IS_NATIVE = Capacitor.isNativePlatform();
const RENDER_ZOOM = IS_NATIVE ? Math.min(window.devicePixelRatio || 1, 2) : 1;
const FPS_TARGET = IS_NATIVE ? 120 : 60;

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  parent: 'game-container',
  backgroundColor: '#050507', // BG_VOID — OLED obsidian (theme.ts, VISUAL-IDENTITY.md §3.1)
  render: {
    antialias: true,
    roundPixels: true,
  },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    // iOS native: render at physical pixel resolution for crisp HiDPI / Retina.
    // Web: stay at logical pixels — Retina rendering combined with the DOM
    // glass overlays was the dominant slowness in v0.5.0.
    zoom: RENDER_ZOOM,
  },
  // 120Hz ProMotion unlock (designer brief §7) — only on the native shell.
  // forceSetTimeOut: false lets Phaser use requestAnimationFrame so it honours
  // the device's native refresh rate. Web is capped at 60 to keep frame budget
  // sane on low-powered laptops and avoid burning battery on a casual mobile
  // browser session.
  // (Xcode-side: Info.plist needs CADisableMinimumFrameDurationOnPhone=YES.)
  fps: {
    target: FPS_TARGET,
    forceSetTimeOut: false,
  },
  scene: [BootScene, MenuScene, GameScene, CodexScene, ShopScene, SettingsScene, HelpScene],
};

const game = new Phaser.Game(config);
(window as unknown as Record<string, unknown>).__PHASER_GAME__ = game;
